import React, { useState, useEffect, useRef } from 'react'
import api from '../services/api'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useModal } from './Modal'

const hrMenuItems = [
  {path:'/tracking',label:'ติดตามสัญญา',icon:'ti-clipboard-list'},
  { path: '/dashboard', label: 'ภาพรวม', icon: 'ti-layout-dashboard' },
  { path: '/contracts', label: 'สัญญาทั้งหมด', icon: 'ti-files' },
  { path: '/contracts/create', label: 'สร้างสัญญาใหม่', icon: 'ti-file-plus' },
  { path: '/approve', label: 'สถานะการลงนาม', icon: 'ti-clipboard-check' },
  { path: '/employees', label: 'พนักงาน', icon: 'ti-users' },
  { path: '/divisions', label: 'กองงาน', icon: 'ti-building-community' },
  { path: '/reports', label: 'รายงานสถิติ', icon: 'ti-chart-dots' },
]

const witnessMenuItems = [
  { path: '/approve', label: 'ลงนามพยาน', icon: 'ti-signature' },
]

const mayorMenuItems = [
  { path: '/mayor', label: 'รอลงนาม', icon: 'ti-writing' },
]

export default function Layout({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { showConfirm, ModalComponents } = useModal()
  const [sidebarOpen, setSidebarOpen] = useState(() => typeof window === 'undefined' || window.innerWidth >= 960)
  const [notifications, setNotifications] = useState([])
  const [showNoti, setShowNoti] = useState(false)
  const [menuSearch, setMenuSearch] = useState('')
  const notiRef = useRef(null)

  const clerkMenuItems = [
    { path: '/clerk/registrations', label: 'ติดตามการลงทะเบียน', icon: 'ti-user-check' },
    { path: '/clerk', label: 'ติดตามสัญญา', icon: 'ti-clipboard-list' },
  ]

  const adminMenuItems = [
    { path: '/admin', label: 'ภาพรวม & Log', icon: 'ti-chart-bar' },
    { path: '/users', label: 'จัดการผู้ใช้', icon: 'ti-users' },
    { path: '/admin/employees', label: 'ข้อมูลพนักงาน', icon: 'ti-user-circle' },
  ]

  const menuItems = user?.role === 'mayor' ? mayorMenuItems
    : user?.role === 'clerk' ? clerkMenuItems
    : user?.role === 'admin' ? adminMenuItems
    : user?.role === 'hr' && user?.is_witness === true ? witnessMenuItems
    : hrMenuItems

  // fetchNotifications intentionally follows the authenticated user only.
  useEffect(() => {
    if (user) {
      fetchNotifications()
      const interval = setInterval(fetchNotifications, 15000)
      const refresh = () => fetchNotifications()
      window.addEventListener('notifications:refresh', refresh)
      return () => {
        clearInterval(interval)
        window.removeEventListener('notifications:refresh', refresh)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  useEffect(() => {
    if (user) fetchNotifications()
  // Refresh the badge whenever the user follows a notification or changes page.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notiRef.current && !notiRef.current.contains(e.target)) setShowNoti(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (window.innerWidth < 960) setSidebarOpen(false)
  }, [location.pathname])

  const fetchNotifications = async () => {
    try {
      const notiList = []
      if (user?.role === 'hr' && user?.is_witness === true) {
        const [signedRes, w1Res] = await Promise.all([
          api.get('/contracts', { params: { status: 'signed', limit: 100 } }),
          api.get('/contracts', { params: { status: 'witness1_signed', limit: 100 } })
        ])
        const signed = signedRes.data?.data?.length || 0
        const w1 = w1Res.data?.data?.length || 0
        if (signed > 0) notiList.push({ icon: 'ti-pencil', color: '#f97316', title: `${signed} สัญญารอพยานคนที่ 1`, message: 'พนักงานเซ็นแล้ว รอลงนามพยาน', link: '/approve' })
        if (w1 > 0) notiList.push({ icon: 'ti-pencil-plus', color: '#3b82f6', title: `${w1} สัญญารอพยานคนที่ 2`, message: 'พยานคนที่ 1 ลงนามแล้ว รอพยานคนที่ 2', link: '/approve' })
      } else if (user?.role === 'hr') {
        const [signedRes, w1Res, expiringRes, regRes] = await Promise.all([
          api.get('/contracts', { params: { status: 'signed', limit: 100 } }),
          api.get('/contracts', { params: { status: 'witness1_signed', limit: 100 } }),
          api.get('/contracts/expiring'),
          api.get('/registration/pending')
        ])
        const signed = signedRes.data?.data?.length || 0
        const w1 = w1Res.data?.data?.length || 0
        const expiring = expiringRes.data?.data?.length || 0
        const regPending = regRes.data?.data?.length || 0
        if (regPending > 0) notiList.push({ icon: 'ti-user-plus', color: '#075dcc', title: `${regPending} รายการลงทะเบียนรอตรวจสอบ`, message: 'พนักงานลงทะเบียนออนไลน์ รอ HR อนุมัติ', link: '/employees?tab=registrations' })
        if (signed > 0) notiList.push({ icon: 'ti-eye', color: '#f97316', title: `${signed} สัญญารอพยานคนที่ 1`, message: 'ติดตามสถานะการลงนาม', link: '/approve' })
        if (w1 > 0) notiList.push({ icon: 'ti-eye', color: '#3b82f6', title: `${w1} สัญญารอพยานคนที่ 2`, message: 'ติดตามสถานะการลงนาม', link: '/approve' })
        if (expiring > 0) notiList.push({ icon: 'ti-alert-triangle', color: '#ef4444', title: `${expiring} สัญญาใกล้หมดอายุ`, message: 'สัญญาจะหมดอายุภายใน 30 วัน', link: '/contracts' })
      }
      if (user?.role === 'mayor') {
        const mayorRes = await api.get('/contracts/mayor-pending')
        const pending = mayorRes.data?.data?.length || 0
        if (pending > 0) notiList.push({ icon: 'ti-writing', color: '#16a34a', title: `${pending} สัญญารอท่านลงนาม`, message: 'HR อนุมัติแล้ว รอนายกลงนาม', link: '/mayor' })
      }
      if (user?.role === 'clerk') {
        const clerkRes = await api.get('/contracts/clerk')
        const pending = clerkRes.data?.data?.filter(c => c.status === 'pending').length || 0
        if (pending > 0) notiList.push({ icon: 'ti-qrcode', color: '#075dcc', title: `${pending} สัญญารอพนักงานลงนาม`, message: 'ติดตามการลงนามของพนักงาน', link: '/clerk' })
      }
      setNotifications(notiList)
    } catch (err) {
      console.error('fetchNotifications error:', err)
    }
  }

  const handleLogout = async () => {
    const confirmed = await showConfirm('info', 'ยืนยันออกจากระบบ', 'ต้องการออกจากระบบใช่หรือไม่?')
    if (confirmed) { logout(); navigate('/login') }
  }
  const closeMobileNavigation = () => {
    if (window.innerWidth < 960) setSidebarOpen(false)
  }

  const roleLabel = user?.role === 'mayor' ? 'นายกเทศมนตรี'
    : user?.role === 'admin' ? 'ผู้ดูแลระบบ'
    : user?.role === 'clerk' ? 'ธุรการ'
    : user?.role === 'hr' && user?.is_witness === true ? 'พยานลงนาม'
    : 'เจ้าหน้าที่ HR'

  const searchResults = menuSearch.trim()
    ? menuItems.filter(item => item.label.toLowerCase().includes(menuSearch.trim().toLowerCase()))
    : []

  const goToMenu = (path) => {
    navigate(path)
    setMenuSearch('')
  }

  const openNotification = (notification) => {
    setShowNoti(false)
    navigate(notification.link)
    window.setTimeout(fetchNotifications, 300)
  }

  return (
    <div className="app-layout" style={{ display: 'flex', height: '100vh', background: '#f3f8ff', fontFamily: 'var(--font-sans)' }}>
      <ModalComponents />

      {sidebarOpen && <button className="mobile-sidebar-backdrop" aria-label="ปิดเมนู" onClick={() => setSidebarOpen(false)} />}

      {/* Sidebar */}
      <aside className={`app-sidebar ${sidebarOpen ? 'is-open' : 'is-closed'}`} style={{
        width: sidebarOpen ? '272px' : '72px',
        background: 'linear-gradient(180deg, #075dcc 0%, #0646a6 55%, #07377f 100%)',
        display: 'flex', flexDirection: 'column',
        transition: 'width 0.2s ease',
        overflow: 'hidden', flexShrink: 0
      }}>
        {/* Logo */}
        <div className="portal-brand" style={{ padding: '20px 18px', borderBottom: '1px solid rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', gap: '12px', minHeight: '88px' }}>
          <div style={{ width: '46px', height: '46px', background: 'rgba(255,255,255,0.16)', border: '1px solid rgba(255,255,255,0.28)', borderRadius: '13px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <i className="ti ti-file-certificate" style={{ color: 'white', fontSize: '22px' }} />
          </div>
          {sidebarOpen && (
            <div style={{ overflow: 'hidden' }}>
              <p style={{ fontSize: '18px', fontWeight: '700', color: 'white', whiteSpace: 'normal' }}>ระบบบริหารจัดการสัญญาจ้างของพนักงานจ้าง</p>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.72)', whiteSpace: 'normal', letterSpacing: '.3px' }}>เทศบาลนครมาบตาพุด</p>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav aria-label="เมนูหลัก" style={{ flex: 1, padding: '18px 12px', overflowY: 'auto' }}>
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path
            return (
              <Link key={item.path} to={item.path} onClick={closeMobileNavigation} style={{ textDecoration: 'none' }}>
                <div className={`portal-nav-item ${isActive ? 'is-active' : ''}`} style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '11px 14px', borderRadius: '12px', marginBottom: '5px',
                  background: isActive ? 'rgba(255,255,255,0.17)' : 'transparent',
                  transition: 'background 0.15s', cursor: 'pointer'
                }}
                  onMouseEnter={e => !isActive && (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                  onMouseLeave={e => !isActive && (e.currentTarget.style.background = 'transparent')}
                >
                  <i className={`ti ${item.icon}`} style={{ fontSize: '21px', color: isActive ? 'white' : 'rgba(255,255,255,0.72)', flexShrink: 0 }} />
                  {sidebarOpen && (
                    <span style={{ fontSize: '16px', color: isActive ? 'white' : 'rgba(255,255,255,0.78)', fontWeight: isActive ? '700' : '500', whiteSpace: 'normal' }}>
                      {item.label}
                    </span>
                  )}
                </div>
              </Link>
            )
          })}
        </nav>

        {/* User */}
        <div style={{ padding: '12px', borderTop: '0.5px solid rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '500', color: 'white', flexShrink: 0 }}>
            {user?.full_name?.charAt(0) || 'U'}
          </div>
          {sidebarOpen && (
            <>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: '14px', fontWeight: '500', color: 'white', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'normal' }}>{user?.full_name}</p>
                <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>{roleLabel}</p>
              </div>
              <button onClick={() => navigate('/change-password')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', padding: '4px' }} title="เปลี่ยนรหัสผ่าน" aria-label="เปลี่ยนรหัสผ่าน">
                <i className="ti ti-key" style={{ fontSize: '16px' }} />
              </button>
              <button onClick={handleLogout} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', padding: '4px' }}
                title="ออกจากระบบ" aria-label="ออกจากระบบ">
                <i className="ti ti-logout" style={{ fontSize: '16px' }} />
              </button>
            </>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="app-main-shell" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Topbar */}
        <header className="app-topbar" style={{ background: 'rgba(255,255,255,0.94)', borderBottom: '1px solid #dbeafe', padding: '0 32px', height: '76px', display: 'flex', alignItems: 'center', gap: '20px', flexShrink: 0 }}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label={sidebarOpen ? 'ย่อเมนู' : 'เปิดเมนู'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px', borderRadius: '6px' }}>
            <i className="ti ti-menu-2" style={{ fontSize: '20px' }} />
          </button>

          <div className="app-topbar-title" style={{ minWidth: '190px' }}>
            <p style={{ fontSize: '17px', fontWeight: '700', color: '#0b3d91' }}>ระบบบริหารจัดการสัญญาจ้างของพนักงานจ้าง</p>
            <p style={{ fontSize: '12px', color: '#64748b' }}>เทศบาลนครมาบตาพุด</p>
          </div>

          <div className="portal-search" style={{ position: 'relative', flex: 1, maxWidth: '520px' }}>
            <i className="ti ti-search" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#2563c5', fontSize: '18px', zIndex: 1 }} />
            <input value={menuSearch} onChange={e => setMenuSearch(e.target.value)} placeholder="ค้นหาเมนูในระบบ..." aria-label="ค้นหาเมนู"
              style={{ width: '100%', height: '42px', padding: '0 14px 0 42px', border: '1px solid #cfe0f6', borderRadius: '12px', background: '#f8fbff', color: '#0f2f5f', outline: 'none', fontSize: '15px' }} />
            {menuSearch.trim() && (
              <div className="portal-search-results">
                {searchResults.length ? searchResults.map(item => (
                  <button key={item.path} onClick={() => goToMenu(item.path)}>
                    <i className={`ti ${item.icon}`} /> <span>{item.label}</span>
                  </button>
                )) : <p>ไม่พบเมนูที่ค้นหา</p>}
              </div>
            )}
          </div>

          {/* Notification */}
          <div style={{ position: 'relative' }} ref={notiRef}>
            <button onClick={() => setShowNoti(!showNoti)} aria-label="การแจ้งเตือน" aria-expanded={showNoti}
              style={{ position: 'relative', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '6px', borderRadius: '8px' }}>
              <i className="ti ti-bell" style={{ fontSize: '20px' }} />
              {notifications.length > 0 && (
                <span style={{ position: 'absolute', top: '4px', right: '4px', width: '16px', height: '16px', background: '#ef4444', color: 'white', fontSize: '10px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '500' }}>
                  {notifications.length > 9 ? '9+' : notifications.length}
                </span>
              )}
            </button>

            {showNoti && (
              <div className="notification-panel" style={{ position: 'absolute', right: 0, top: '44px', width: '320px', background: 'white', borderRadius: '14px', boxShadow: '0 8px 30px rgba(0,0,0,0.12)', border: '0.5px solid #e2e8f0', zIndex: 50, overflow: 'hidden' }}>
                <div style={{ padding: '14px 16px', borderBottom: '0.5px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <p style={{ fontSize: '13px', fontWeight: '500', color: '#0f172a' }}>การแจ้งเตือน</p>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>{notifications.length} รายการ</span>
                </div>
                <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
                  {notifications.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '32px', color: '#94a3b8', fontSize: '13px' }}>ไม่มีการแจ้งเตือน</div>
                  ) : notifications.map((n, i) => (
                    <button type="button" key={`${n.link}-${i}`} onClick={() => openNotification(n)}
                      style={{ width:'100%', padding: '12px 16px', cursor: 'pointer', border:'none', borderBottom: '0.5px solid #f8fafc', background:'transparent', textAlign:'left', display: 'flex', gap: '12px', alignItems: 'flex-start' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: `${n.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className={`ti ${n.icon}`} style={{ fontSize: '16px', color: n.color }} />
                      </div>
                      <div>
                        <p style={{ fontSize: '12px', fontWeight: '500', color: '#0f172a' }}>{n.title}</p>
                        <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{n.message}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Content */}
        <main className="app-content" style={{ flex: 1, overflowY: 'auto', padding: '32px' }}>
          {children}
        </main>
      </div>
      <style>{`
        .mobile-sidebar-backdrop { display: none; }
        .portal-nav-item { position: relative; }
        .portal-nav-item.is-active::before {
          content: ''; position: absolute; left: -8px; top: 7px; bottom: 7px;
          width: 4px; border-radius: 0 4px 4px 0; background: #1479d4;
          box-shadow: 0 0 12px rgba(20,121,212,.18);
        }
        .portal-search-results {
          position: absolute; inset: calc(100% + 8px) 0 auto 0; z-index: 80;
          background: white; border: 1px solid #dbeafe; border-radius: 13px;
          box-shadow: 0 16px 38px rgba(15,64,131,.16); padding: 6px;
        }
        .portal-search-results button { width: 100%; min-height: 40px; display: flex; align-items: center; gap: 10px; border: 0; border-radius: 9px; background: transparent; color: #174989; padding: 8px 11px; cursor: pointer; text-align: left; }
        .portal-search-results button:hover { background: #eff6ff; transform: none; }
        .portal-search-results p { padding: 12px; color: #94a3b8; font-size: 12px; }
        @media (max-width: 959px) {
          .app-layout { width: 100%; height: 100svh !important; overflow: hidden; }
          .app-sidebar {
            position: fixed;
            inset: 0 auto 0 0;
            z-index: 1000;
            width: min(82vw, 290px) !important;
            height: 100svh;
            box-shadow: 12px 0 36px rgba(15,23,42,0.24);
            transform: translateX(-105%);
            transition: transform 0.22s ease !important;
          }
          .app-sidebar.is-open { transform: translateX(0); }
          .app-sidebar.is-closed { transform: translateX(-105%); }
          .mobile-sidebar-backdrop {
            display: block;
            position: fixed;
            inset: 0;
            z-index: 999;
            border: 0;
            background: rgba(15,23,42,0.46);
          }
          .app-main-shell { width: 100%; min-width: 0; }
          .app-topbar { height: 58px !important; padding: 0 12px !important; gap: 8px !important; }
          .app-topbar-title {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            font-size: 12px !important;
          }
          .app-topbar-title p:first-child { font-size: 13px !important; }
          .app-topbar-title p:last-child { display: none; }
          .portal-search { display: none; }
          .app-content { padding: 14px !important; }
          .notification-panel { width: min(330px, calc(100vw - 28px)) !important; right: -4px !important; }
          .app-content table { min-width: 680px; }
          .app-content button, .app-content input, .app-content select { min-height: 42px; }
        }
      `}</style>
    </div>
  )
}
